import { useEffect, useState } from 'react';
import { basedAxios } from '../../store/Store';
import { File, Folder } from './types';
import { FileItem } from './components/file-item';
import SystemLabel from '../../components/systemlabel/SystemLabel';

interface FileCatalogProps {
  environment: {
    eventId: number;
  };
  settings: {
    rootPath: string;
    hideNavigation?: boolean;
  };
}

export function FileCatalog({ settings: { rootPath, hideNavigation = false }, environment: { eventId } }: FileCatalogProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState(rootPath);
  const [folderState, setFolderState] = useState<{
    [key: string]: boolean;
  }>({});

  const bucketPrefix = `https://jirangopub.s3.eu-north-1.amazonaws.com/Files/${eventId}/`;

  useEffect(() => {
    (async () => {
      try {
        const response = await basedAxios.get('./files', {
          params: {
            path: rootPath,
            eventId,
          },
        });

        setFiles(response.data?.files ?? []);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const filteredFiles = files?.filter((file: File) => {
    const id = file.url
      ?.replace(bucketPrefix, '')
      ?.split('/')
      ?.filter(part => part !== file.name)
      ?.join('/');

    if (file.name.includes('FAPLACEHOLDER.HIDDEN')) {
      return false;
    }

    if (id.split('/').length > 2) {
      const newId = id
        .split('/')
        .slice(id.split('/').length - 2, id.split('/').length)
        .join('/');

      debugger;

      return newId === (currentPath === '_' ? '' : currentPath.replace('_/', '')) || newId.endsWith(currentPath.replace('_/', ''));
    }

    return id === (currentPath === '_' ? '' : currentPath.replace('_/', ''));
  });

  const folders = files.reduce<Folder[]>((acc, file: File) => {
    const parts = file.url
      ?.replace(bucketPrefix, '')
      ?.split('/')
      ?.filter(part => part !== file.name);

    const isRoot = parts?.length === 0;

    const parentFolder = isRoot ? null : parts?.[parts.length - 2] ?? '_';
    const folder = parts?.[parts.length - 1] ?? (isRoot ? '_' : '');
    const fullPath = isRoot ? '_' : parts?.join('/');
    const id = isRoot ? '_' : `${parentFolder}/${folder}`;

    // Avoid adding duplicates
    if (!acc.some(f => f.folder === folder && f.parentFolder === parentFolder)) {
      acc.push({ folder, parentFolder, fullPath, id });
    }

    return acc;
  }, []);

  const renderFolder = ({ folder, id }: Folder): JSX.Element | null => {
    // Get folders with the specified parent
    const isRoot = folder === '_' || folder === rootPath;
    const subFolders = folders.filter(f => f.parentFolder === folder && f.id !== id);

    if (subFolders.length === 0) {
      return (
        <p onClick={() => setCurrentPath(id)} className={'cursor-pointer' + (rootPath === currentPath ? ' !font-bold' : '')}>
          <i className="ri-folder-line"></i> {rootPath}
        </p>
      );
    }

    return (
      <div>
        {isRoot && (
          <p onClick={() => setCurrentPath(id)} className={'cursor-pointer' + (rootPath === currentPath ? ' !font-bold' : '')}>
            <i className="ri-folder-line"></i> {rootPath}
          </p>
        )}
        {subFolders.map(subFolder => (
          <div className="ml-4 " key={subFolder.folder}>
            <div className="flex">
              {folders.some(f => f.parentFolder === subFolder.folder && f.id !== id) && (
                <div
                  className="cursor-pointer"
                  onClick={() => setFolderState(prev => ({ ...prev, [`${id}/${subFolder.folder}`]: !folderState?.[`${id}/${subFolder.folder}`] }))}
                >
                  {folderState?.[`${id}/${subFolder.folder}`] ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-right-s-line"></i>}
                </div>
              )}
              <p
                onClick={() => setCurrentPath(subFolder.id)}
                className={'cursor-pointer hover:underline' + (subFolder.id === currentPath ? ' !font-bold' : '')}
              >
                <i className="ri-folder-line"></i> {subFolder.folder}
              </p>
            </div>
            <div className="ml-4 flex flex-col">{folderState?.[`${id}/${subFolder.folder}`] && renderFolder(subFolder)}</div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex w-full divide-x divide-inherit border border-slate-200 list-view !min-h-[500px] ">
        <div className="p-4 grid grid-cols-1 gap-1 h-fit w-full animate-pulse">
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
          <div className="h-[16px] bg-zinc-400 w-full mb-2.5" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-2">
        <i className="ri-error-warning-line"></i> <SystemLabel name="SomethingWentWrong" groupName="CMS.FileCatalog" />
      </div>
    );
  }

  return (
    <div className="flex w-full divide-y lg:divide-x divide-inherit border lg:flex-row flex-col border-slate-200 min-h-[500px] list-view">
      {!hideNavigation && (
        <div className="flex flex-col gap-y-2 w-fit p-4 max-h-full overflow-y-auto">
          <div>
            {renderFolder({
              folder: rootPath !== '' ? rootPath : '_',
              parentFolder: rootPath !== '' ? rootPath : null,
              fullPath: rootPath !== '' ? rootPath : '_',
              id: rootPath !== '' ? rootPath : '_',
            })}
          </div>
        </div>
      )}
      <div className=" overflow-y-auto flex-1 max-w-[calc(100%)] max-h-full w-auto">
        <div className="h-fit p-4 ">
          {filteredFiles.map((file: File) => (
            <FileItem key={file.url} {...file} />
          ))}
        </div>
      </div>
    </div>
  );
}
